/**
 * Rate Limiter utility for OpenAI API requests
 * Implements token bucket algorithm with sliding window
 */

class RateLimiter {
  constructor(config = {}) {
    this.requestsPerMinute = config?.requestsPerMinute || 60;
    this.requestsPerHour = config?.requestsPerHour || 3600;
    this.tokensPerMinute = config?.tokensPerMinute || 90000;
    this.tokensPerHour = config?.tokensPerHour || 540000;
    
    // Request tracking
    this.minuteWindow = [];
    this.hourWindow = [];
    
    // Token tracking
    this.minuteTokens = [];
    this.hourTokens = [];
    
    // Queue for pending requests
    this.requestQueue = [];
    this.isProcessingQueue = false;
    
    // Rate limit status
    this.rateLimitStatus = {
      minuteRequests: 0,
      hourRequests: 0,
      minuteTokens: 0,
      hourTokens: 0,
      isLimited: false,
      resetTime: null,
    };
  }

  /**
   * Clean up old entries from tracking windows
   */
  cleanupWindows(now) {
    const oneMinuteAgo = now - 60000;
    const oneHourAgo = now - 3600000;
    
    // Clean minute windows
    this.minuteWindow = this.minuteWindow?.filter(time => time > oneMinuteAgo);
    this.minuteTokens = this.minuteTokens?.filter(entry => entry?.timestamp > oneMinuteAgo);
    
    // Clean hour windows
    this.hourWindow = this.hourWindow?.filter(time => time > oneHourAgo);
    this.hourTokens = this.hourTokens?.filter(entry => entry?.timestamp > oneHourAgo);
  }

  /**
   * Calculate current usage
   */
  getCurrentUsage(now) {
    this.cleanupWindows(now);
    
    return {
      minuteRequests: this.minuteWindow?.length || 0,
      hourRequests: this.hourWindow?.length || 0,
      minuteTokens: this.minuteTokens?.reduce((sum, entry) => sum + (entry?.tokens || 0), 0),
      hourTokens: this.hourTokens?.reduce((sum, entry) => sum + (entry?.tokens || 0), 0),
    };
  }

  /**
   * Check if request can be made within rate limits
   */
  canMakeRequest(estimatedTokens = 0) {
    const now = Date.now();
    const usage = this.getCurrentUsage(now);
    
    // Check request limits
    const withinMinuteRequests = usage?.minuteRequests < this.requestsPerMinute;
    const withinHourRequests = usage?.hourRequests < this.requestsPerHour;
    
    // Check token limits
    const withinMinuteTokens = (usage?.minuteTokens + estimatedTokens) < this.tokensPerMinute;
    const withinHourTokens = (usage?.hourTokens + estimatedTokens) < this.tokensPerHour;
    
    return {
      allowed: withinMinuteRequests && withinHourRequests && withinMinuteTokens && withinHourTokens,
      reason: !withinMinuteRequests ? 'minute_requests' : !withinHourRequests ?'hour_requests': !withinMinuteTokens ?'minute_tokens': !withinHourTokens ?'hour_tokens' : null,
      usage,
    };
  }

  /**
   * Record a completed request
   */
  recordRequest(tokensUsed = 0) {
    const now = Date.now();
    
    // Record request
    this.minuteWindow?.push(now);
    this.hourWindow?.push(now);
    
    // Record tokens if provided
    if (tokensUsed > 0) {
      const tokenEntry = { timestamp: now, tokens: tokensUsed };
      this.minuteTokens?.push(tokenEntry);
      this.hourTokens?.push(tokenEntry);
    }
    
    // Update status
    this.updateStatus();
  }

  /**
   * Update rate limit status
   */
  updateStatus() {
    const now = Date.now();
    const usage = this.getCurrentUsage(now);
    
    this.rateLimitStatus = {
      ...usage,
      isLimited: usage?.minuteRequests >= this.requestsPerMinute || 
                 usage?.hourRequests >= this.requestsPerHour ||
                 usage?.minuteTokens >= this.tokensPerMinute ||
                 usage?.hourTokens >= this.tokensPerHour,
      resetTime: this.calculateResetTime(now, usage),
    };
  }

  /**
   * Calculate when rate limits will reset
   */
  calculateResetTime(now, usage) {
    const resetTimes = [];
    
    if (usage?.minuteRequests >= this.requestsPerMinute || usage?.minuteTokens >= this.tokensPerMinute) {
      const oldestMinute = Math.min(
        this.minuteWindow?.[0] || now,
        this.minuteTokens?.[0]?.timestamp || now
      );
      resetTimes?.push(oldestMinute + 60000);
    }
    
    if (usage?.hourRequests >= this.requestsPerHour || usage?.hourTokens >= this.tokensPerHour) {
      const oldestHour = Math.min(
        this.hourWindow?.[0] || now,
        this.hourTokens?.[0]?.timestamp || now
      );
      resetTimes?.push(oldestHour + 3600000);
    }
    
    return resetTimes?.length > 0 ? Math.min(...resetTimes) : null;
  }

  /**
   * Get current rate limit status
   */
  getStatus() {
    this.updateStatus();
    return {
      ...this.rateLimitStatus,
      limits: {
        requestsPerMinute: this.requestsPerMinute,
        requestsPerHour: this.requestsPerHour,
        tokensPerMinute: this.tokensPerMinute,
        tokensPerHour: this.tokensPerHour,
      },
      remaining: {
        minuteRequests: Math.max(0, this.requestsPerMinute - this.rateLimitStatus?.minuteRequests),
        hourRequests: Math.max(0, this.requestsPerHour - this.rateLimitStatus?.hourRequests),
        minuteTokens: Math.max(0, this.tokensPerMinute - this.rateLimitStatus?.minuteTokens),
        hourTokens: Math.max(0, this.tokensPerHour - this.rateLimitStatus?.hourTokens),
      },
    };
  }

  /**
   * Wait until rate limit resets
   */
  async waitForReset() {
    const status = this.getStatus();
    if (!status?.isLimited) return;
    
    const waitTime = status?.resetTime - Date.now();
    if (waitTime > 0) {
      console.log(`Rate limit reached. Waiting ${Math.ceil(waitTime / 1000)}s until reset...`);
      await new Promise(resolve => setTimeout(resolve, waitTime));
    }
  }

  /**
   * Process queued requests
   */
  async processQueue() {
    if (this.isProcessingQueue || this.requestQueue?.length === 0) return;
    
    this.isProcessingQueue = true;
    
    while (this.requestQueue?.length > 0) {
      const { fn, estimatedTokens, resolve, reject } = this.requestQueue?.[0];
      
      const check = this.canMakeRequest(estimatedTokens);
      
      if (check?.allowed) {
        this.requestQueue?.shift();
        
        try {
          const result = await fn();
          this.recordRequest(result?.tokensUsed || estimatedTokens);
          resolve(result);
        } catch (error) {
          reject(error);
        }
      } else {
        // Wait before checking again
        await this.waitForReset();
      }
    }
    
    this.isProcessingQueue = false;
  }

  /**
   * Execute function with rate limiting
   */
  async execute(fn, options = {}) {
    const estimatedTokens = options?.estimatedTokens || 0;
    const queueIfLimited = options?.queueIfLimited !== false;
    
    const check = this.canMakeRequest(estimatedTokens);
    
    if (check?.allowed) {
      try {
        const result = await fn();
        this.recordRequest(result?.tokensUsed || estimatedTokens);
        return result;
      } catch (error) {
        throw error;
      }
    } else {
      if (queueIfLimited) {
        // Add to queue and wait
        return new Promise((resolve, reject) => {
          this.requestQueue?.push({ fn, estimatedTokens, resolve, reject });
          this.processQueue();
        });
      } else {
        // Throw rate limit error immediately
        const status = this.getStatus();
        const error = new Error('Rate limit exceeded');
        error.code = 'RATE_LIMIT_EXCEEDED';
        error.details = {
          reason: check?.reason,
          usage: check?.usage,
          resetTime: status?.resetTime,
          waitSeconds: status?.resetTime ? Math.ceil((status?.resetTime - Date.now()) / 1000) : 0,
        };
        throw error;
      }
    }
  }

  /**
   * Reset all rate limit counters
   */
  reset() {
    this.minuteWindow = [];
    this.hourWindow = [];
    this.minuteTokens = [];
    this.hourTokens = [];
    this.requestQueue = [];
    this.isProcessingQueue = false;
    this.updateStatus();
  }
}

// Create singleton instance with default OpenAI free tier limits
const defaultRateLimiter = new RateLimiter({
  requestsPerMinute: 60,      // OpenAI free tier: 60 RPM
  requestsPerHour: 3600,      // OpenAI free tier: 3600 RPH
  tokensPerMinute: 90000,     // OpenAI free tier: 90k TPM
  tokensPerHour: 540000,      // Conservative hourly limit
});

/**
 * Wrap an async function with rate limiting
 */
export function withRateLimit(fn, options = {}) {
  return async (...args) => {
    return defaultRateLimiter?.execute(() => fn(...args), options);
  };
}

/**
 * Get rate limiter status
 */
export function getRateLimitStatus() {
  return defaultRateLimiter?.getStatus();
}

/**
 * Reset rate limiter
 */
export function resetRateLimiter() {
  defaultRateLimiter?.reset();
}

/**
 * Create custom rate limiter instance
 */
export function createRateLimiter(config) {
  return new RateLimiter(config);
}

export { RateLimiter };
export default defaultRateLimiter;